import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { AssistantFollowUpModelData } from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { IUpdateAssistantFollowUpOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData } from "@/store/sync/types";
import { getLoggableOperation } from "@/store/sync/utils";

const hasPayloadField = (
  payload: IUpdateAssistantFollowUpOperation["payload"],
  field: keyof IUpdateAssistantFollowUpOperation["payload"]
) => Object.prototype.hasOwnProperty.call(payload, field);

export class UpdateAssistantFollowUpOperation extends BaseSyncOperation<IUpdateAssistantFollowUpOperation> {
  get operationKind(): "UPDATE_ASSISTANT_FOLLOW_UP" {
    return "UPDATE_ASSISTANT_FOLLOW_UP";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const existingFollowUp = await this.store.assistantFollowUps.getAsync(this.payload.id);
    if (!existingFollowUp) {
      logger.error({
        message: "Attempted to update assistant follow-up that does not exist",
        info: {
          operation: getLoggableOperation(this),
        },
      });
      return [];
    }

    const modelData = existingFollowUp.modelData;
    const followUpUpdate = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "ASSISTANT_FOLLOW_UP",
        model_version: existingFollowUp.modelVersion,
        model_data: {
          ...modelData,
          content: this.payload.content ?? modelData.content,
          status: this.payload.status ?? modelData.status,
          scheduled_for: this.payload.scheduled_for ?? modelData.scheduled_for,
          project_id:
            this.payload.clear_project_id || hasPayloadField(this.payload, "project_id")
              ? (this.payload.project_id ?? null)
              : modelData.project_id,
          task_id:
            this.payload.clear_task_id || hasPayloadField(this.payload, "task_id")
              ? (this.payload.task_id ?? null)
              : modelData.task_id,
          trigger_type: hasPayloadField(this.payload, "trigger_type")
            ? (this.payload.trigger_type ?? null)
            : modelData.trigger_type,
          commitment_kind: hasPayloadField(this.payload, "commitment_kind")
            ? (this.payload.commitment_kind ?? null)
            : modelData.commitment_kind,
          due_at: hasPayloadField(this.payload, "due_at")
            ? (this.payload.due_at ?? null)
            : modelData.due_at,
          deadline_at: hasPayloadField(this.payload, "deadline_at")
            ? (this.payload.deadline_at ?? null)
            : modelData.deadline_at,
          trigger_not_before: hasPayloadField(this.payload, "trigger_not_before")
            ? (this.payload.trigger_not_before ?? null)
            : modelData.trigger_not_before,
          trigger_not_after: hasPayloadField(this.payload, "trigger_not_after")
            ? (this.payload.trigger_not_after ?? null)
            : modelData.trigger_not_after,
          fired_at: hasPayloadField(this.payload, "fired_at")
            ? (this.payload.fired_at ?? null)
            : modelData.fired_at,
          completed_at: hasPayloadField(this.payload, "completed_at")
            ? (this.payload.completed_at ?? null)
            : modelData.completed_at,
          updated_at: this.committedAt,
        },
        model_scopes: existingFollowUp.modelScopes,
      },
    } as unknown as OptimisticSyncUpdate<AssistantFollowUpModelData>;

    return [followUpUpdate];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.assistantFollowUps.recompute(this.payload.id);
  }
}
